import React, { useState, useEffect } from "react";
import { GetWaterProfileOrders } from "../../services/ClientWaterProfileService";
import { NotificationManager } from "react-notifications";
import CommonLoader from "../common/CommonLoader";
import OrderManager from "./OrderManager"; // Import the new component
import {
  Box,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

export default function ComparisonArea({
  selectedOrders = [],
  selectedWaterProfile = null,
  onRemoveOrder,
  onRemoveWaterProfile,
  onSelectOrders,
  onSelectWaterProfile,
  accountNumber, // Add accountNumber prop
  className = "",
}) {
  const [loading, setLoading] = useState(false);
  const [profileOrdersData, setProfileOrdersData] = useState([]);
  const [internalSelectedOrders, setInternalSelectedOrders] =
    useState(selectedOrders);

  const hasSelections =
    internalSelectedOrders.length > 0 || selectedWaterProfile;
  const hasData = profileOrdersData.length > 0 && selectedWaterProfile;

  // Chemical properties mapping
  const chemicalProperties = {
    calcium: { label: "Calcium", unit: "mg/L" },
    totalAlkalinity: { label: "Total Alkalinity", unit: "mg/L" },
    bicarbonate: { label: "Bicarbonate", unit: "mg/L" },
    sulfate: { label: "Sulfate", unit: "mg/L" },
    magnesium: { label: "Magnesium", unit: "mg/L" },
    totalHardness: { label: "Total Hardness", unit: "mg/L" },
    iron: { label: "Iron", unit: "mg/L" },
    pH: { label: "pH", unit: "" },
    totalChlorine: { label: "Total Chlorine", unit: "mg/L" },
    residualAlkalinity: { label: "Residual Alkalinity", unit: "mg/L" },
    chloride: { label: "Chloride", unit: "mg/L" },
    sulfateToChlorideRatio: { label: "Sulfate/Chloride Ratio", unit: "" },
    sodium: { label: "Sodium", unit: "mg/L" },
    zinc: { label: "Zinc", unit: "mg/L" },
    potassium: { label: "Potassium", unit: "mg/L" },
    copper: { label: "Copper", unit: "mg/L" },
    tds: { label: "TDS", unit: "mg/L" },
  };

  // Sync internal selected orders with props when they change
  useEffect(() => {
    setInternalSelectedOrders(selectedOrders);
  }, [selectedOrders]);

  // Fetch profile orders whenever internal selected orders change
  useEffect(() => {
    fetchProfileOrders();
  }, [internalSelectedOrders]);

  const fetchProfileOrders = async () => {
    if (!internalSelectedOrders || internalSelectedOrders.length === 0) {
      setProfileOrdersData([]);
      return;
    }

    try {
      setLoading(true);
      const orderNumbersCsv = internalSelectedOrders.join(",");
      const response = await GetWaterProfileOrders(orderNumbersCsv);
      setProfileOrdersData(response || []);
      console.log("profile Orders Data response : ", response);
    } catch (error) {
      console.error("Error fetching profile Orders Data:", error);
      NotificationManager.error("Error fetching profile orders Data", "Error");
    } finally {
      setLoading(false);
    }
  };

  // Handle order changes from OrderManager
  const handleOrdersChange = (newOrders) => {
    // Update internal state
    setInternalSelectedOrders(newOrders);

    // Call the parent's onSelectOrders if provided to keep parent state in sync
    if (onSelectOrders) {
      onSelectOrders(newOrders);
    }

    // fetchProfileOrders will be called automatically via useEffect
  };

  // Get chemicals that have data in either profile or orders
  const getAvailableChemicals = () => {
    const chemicals = [];

    Object.keys(chemicalProperties).forEach((chemicalKey) => {
      const profileValue = selectedWaterProfile?.[chemicalKey];
      const hasOrderData = profileOrdersData.some(
        (order) =>
          order[chemicalKey] !== null && order[chemicalKey] !== undefined
      );

      if (
        (profileValue !== null && profileValue !== undefined) ||
        hasOrderData
      ) {
        chemicals.push({
          key: chemicalKey,
          ...chemicalProperties[chemicalKey],
        });
      }
    });

    return chemicals;
  };

  const getVarianceColor = (profileValue, orderValue) => {
    if (!profileValue || !orderValue) return "";

    const variance = Math.abs(profileValue - orderValue);
    const percentVariance = (variance / profileValue) * 100;

    if (percentVariance > 20) return "error";
    if (percentVariance > 10) return "warning";
    return "success";
  };

  const getVariancePercentage = (profileValue, orderValue) => {
    if (!profileValue || !orderValue) return null;
    const variance = Math.abs(profileValue - orderValue);
    return ((variance / profileValue) * 100).toFixed(1);
  };

  const formatValue = (value) => {
    if (value === null || value === undefined) return "-";
    return typeof value === "number" ? value.toFixed(2) : value;
  };

  const renderSelectionArea = () => (
    <Paper sx={{ p: 2, mb: 3 }}>
      {!hasSelections ? (
        <Box sx={{ textAlign: "center", py: 6 }}>
          <Typography color="text.secondary">
            No items selected for comparison yet.
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Select orders and water profiles to start comparing.
          </Typography>
        </Box>
      ) : (
        <>
          <OrderManager
            selectedOrders={internalSelectedOrders}
            onOrdersChange={handleOrdersChange}
            accountNumber={accountNumber}
            maxSelection={10}
          />

          {selectedWaterProfile && (
            <Box sx={{ mt: 3 }}>
              <Typography
                variant="subtitle1"
                sx={{ color: "primary.main", fontWeight: 700, mb: 1.5 }}
              >
                Selected Water Profile
              </Typography>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                  <Chip
                    size="small"
                    label={`ID: ${selectedWaterProfile.id || "N/A"}`}
                  />
                  <Chip
                    size="small"
                    color="info"
                    variant="outlined"
                    label={`Type: ${selectedWaterProfile.type || "Unknown Type"}`}
                  />
                </Stack>
              </Paper>
            </Box>
          )}
        </>
      )}
    </Paper>
  );

  const renderComparisonTable = () => {
    if (loading) {
      return <CommonLoader loading={loading} />;
    }

    if (!hasData) {
      return (
        <Paper sx={{ p: 2, textAlign: "center", py: 6 }}>
          <Typography color="text.secondary">
            No comparison data available.
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Select both orders and a water profile to view comparison results.
          </Typography>
        </Paper>
      );
    }

    const availableChemicals = getAvailableChemicals();

    if (availableChemicals.length === 0) {
      return (
        <Paper sx={{ p: 2, textAlign: "center", py: 6 }}>
          <Typography color="text.secondary">
            No chemical data available for comparison.
          </Typography>
        </Paper>
      );
    }

    const orderDataMap = {};
    profileOrdersData.forEach((order) => {
      orderDataMap[order.orderNumber] = order;
    });

    return (
      <Paper sx={{ p: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
            mb: 2,
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{ color: "primary.main", fontWeight: 700 }}
          >
            Comparison Results
          </Typography>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              color="info"
              label={`${availableChemicals.length} Chemical${
                availableChemicals.length !== 1 ? "s" : ""
              } Available`}
            />
            <Chip size="small" color="success" label="Good Match (<10%)" />
            <Chip size="small" color="warning" label="Moderate (10-20%)" />
            <Chip size="small" color="error" label="High Variance (>20%)" />
          </Stack>
        </Box>

        <TableContainer sx={{ maxHeight: 600 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    position: "sticky",
                    left: 0,
                    zIndex: 3,
                    backgroundColor: "primary.main",
                    color: "primary.contrastText",
                    fontWeight: 600,
                    minWidth: 140,
                  }}
                >
                  Chemical
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    backgroundColor: "primary.main",
                    color: "primary.contrastText",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                  }}
                >
                  Profile Value
                </TableCell>
                {internalSelectedOrders.map((orderNumber) => (
                  <TableCell
                    key={orderNumber}
                    align="center"
                    sx={{
                      backgroundColor: "primary.main",
                      color: "primary.contrastText",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      minWidth: 120,
                    }}
                  >
                    Order {orderNumber}
                    <Typography
                      variant="caption"
                      component="div"
                      sx={{ opacity: 0.85 }}
                    >
                      Value | Variance
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {availableChemicals.map((chemical) => {
                const profileValue = selectedWaterProfile[chemical.key];

                return (
                  <TableRow key={chemical.key} hover>
                    <TableCell
                      sx={{
                        position: "sticky",
                        left: 0,
                        zIndex: 1,
                        backgroundColor: "background.paper",
                        fontWeight: 600,
                        minWidth: 140,
                      }}
                    >
                      {chemical.label}
                      {chemical.unit && (
                        <Typography
                          variant="caption"
                          component="div"
                          color="text.secondary"
                        >
                          ({chemical.unit})
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell align="center">
                      <Chip
                        size="small"
                        color="primary"
                        label={formatValue(profileValue)}
                      />
                    </TableCell>

                    {internalSelectedOrders.map((orderNumber) => {
                      const orderData = orderDataMap[orderNumber];
                      const orderValue = orderData
                        ? orderData[chemical.key]
                        : null;
                      const variancePercent = getVariancePercentage(
                        profileValue,
                        orderValue
                      );
                      const severity = getVarianceColor(
                        profileValue,
                        orderValue
                      );

                      return (
                        <TableCell key={orderNumber} align="center">
                          {orderValue !== null && orderValue !== undefined ? (
                            <Box
                              sx={{
                                borderRadius: 1,
                                py: 0.5,
                                px: 1,
                                display: "inline-block",
                                minWidth: 70,
                                ...(severity && {
                                  backgroundColor: `${severity}.light`,
                                  color: `${severity}.contrastText`,
                                }),
                              }}
                            >
                              <Box sx={{ fontWeight: 600 }}>
                                {formatValue(orderValue)}
                              </Box>
                              {variancePercent && (
                                <Typography variant="caption" component="div">
                                  ±{variancePercent}%
                                </Typography>
                              )}
                            </Box>
                          ) : (
                            <Chip size="small" label="-" />
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  };

  return (
    <Box className={className}>
      {renderSelectionArea()}
      {renderComparisonTable()}
    </Box>
  );
}
